import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Card, IconCheckCircle, IconInfoCircle } from '@indipia/ui-core';
import {
  SHARE_LOAD_TIMEOUT_MS,
  ShareLoadSkeleton,
  ShareNoticeScreen,
  ShareStateScreen,
  classifyShareError,
  formatSharePhone,
  isValidPhone,
  useShareResource,
} from '@indipia/shared-features';
import { receiptShareApi } from '../api/receiptShareApi';
import { ReceiptForm } from '../components/ReceiptForm';
import {
  RECEIPT_TEXT,
  arrivalText,
  itemText,
  loadErrorToView,
  qtyCheckedText,
  screenKindOf,
  toReceiptView,
  type ReceiptView,
  type ReceivedSummary,
} from './receiptRegister.logic';

/**
 * 반장 입고 사진 등록 (C-4-2, 외부·무인증 공유 화면) — `share/FOREMAN_RECEIPT`.
 *
 * 도착 안내 알림톡의 [입고사진 등록하기] 버튼으로 `/share/FOREMAN_RECEIPT?shareId=...` 진입(F-04 §7).
 * 자격증명은 공유 쿠키(share_jwt)뿐이며 이 화면은 토큰·shareId 를 다루지 않는다.
 * 서버 신호(state·target)가 없거나 약속 밖이면 폴백 없이 오류 화면(UNKNOWN)으로 보낸다.
 */

type ScreenSource = 'load' | 'local';

function Shell({ children }: { children: ReactNode }) {
  return (
    // data-group="03" = 인디플래너 그룹 액센트(지시서 §13 M9)
    <div data-group="03" className="min-h-screen" style={{ background: 'var(--shell-bg)' }}>
      <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col gap-4 p-4" style={{ background: 'var(--shell-bg)' }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 text-body-sm">
      <dt className="w-16 shrink-0 text-text-muted">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-text-primary">{value}</dd>
    </div>
  );
}

/** 폼 화면 제목 — 마운트(화면 전환) 시 h1 로 포커스 이동(§4.1). */
function OpenHeader({ companyName, foremanName }: { companyName?: string | null; foremanName?: string | null }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  return (
    <header className="flex flex-col gap-1">
      <h1 ref={titleRef} tabIndex={-1} className="text-heading-sm font-bold text-text-primary">
        {RECEIPT_TEXT.heading}
      </h1>
      {companyName && <p className="text-body-sm text-text-secondary">{companyName}</p>}
      {foremanName && <p className="text-body-sm text-text-secondary">{RECEIPT_TEXT.headerForeman(foremanName)}</p>}
    </header>
  );
}

function ReceivedScreen({ summary }: { summary: ReceivedSummary | null }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const qtyText = summary ? qtyCheckedText(summary.qtyCheckedCode) : null;
  const lines: Array<[string, string]> = [];
  if (summary) {
    const at = summary.receiptAtText ?? summary.receiptDateText;
    if (at) lines.push([summary.receiptAtText ? RECEIPT_TEXT.rowReceiptAt : RECEIPT_TEXT.rowReceiptDate, at]);
    if (summary.photoCnt != null) lines.push([RECEIPT_TEXT.rowPhotoCount, RECEIPT_TEXT.photoCountValue(summary.photoCnt)]);
    if (qtyText) lines.push([RECEIPT_TEXT.rowQtyChecked, qtyText]);
  }
  return (
    <Shell>
      <div className="flex flex-col items-center gap-2 pt-8 text-center" aria-live="polite">
        <span style={{ color: 'var(--color-success)' }} aria-hidden="true">
          <IconCheckCircle width={44} height={44} />
        </span>
        <h1 ref={titleRef} tabIndex={-1} className="text-heading-sm font-bold text-text-primary">
          {RECEIPT_TEXT.doneTitle}
        </h1>
        <p className="text-body-sm text-text-secondary">{RECEIPT_TEXT.doneDesc}</p>
      </div>
      {lines.length > 0 && (
        <Card>
          <dl className="flex flex-col gap-2">
            {lines.map(([l, v]) => (
              <Row key={l} label={l} value={v} />
            ))}
          </dl>
        </Card>
      )}
      <p className="text-center text-caption text-text-muted">{RECEIPT_TEXT.doneFooter}</p>
    </Shell>
  );
}

export function ReceiptRegisterPage() {
  const res = useShareResource<ReceiptView>(async (signal) => {
    try {
      const r = await receiptShareApi.getReceiptShare(SHARE_LOAD_TIMEOUT_MS, signal);
      return toReceiptView(r.data?.data);
    } catch (e) {
      // 전용 화면으로 가는 업무 오류(§10.3)는 화면 뷰로 변환, 그 밖은 훅에 맡긴다.
      const view = loadErrorToView(classifyShareError(e));
      if (view) return view;
      throw e;
    }
  });
  const [override, setOverride] = useState<{ view: ReceiptView; source: ScreenSource } | null>(null);

  useEffect(() => {
    document.title = RECEIPT_TEXT.documentTitle;
  }, []);

  const showView = (view: ReceiptView) => setOverride({ view, source: 'local' });

  if (res.status === 'loading') {
    return (
      <Shell>
        <ShareLoadSkeleton />
      </Shell>
    );
  }

  if (res.status === 'error' || !res.data) {
    const kind = screenKindOf(res.error?.kind ?? 'UNKNOWN');
    return (
      <ShareStateScreen
        kind={kind}
        onRetry={kind === 'NETWORK' || kind === 'SERVER' || kind === 'UNKNOWN' ? res.reload : undefined}
      />
    );
  }

  const view = override?.view ?? res.data;
  const fromLocal = override != null;

  switch (view.t) {
    case 'screen':
      return (
        <ShareStateScreen
          kind={view.kind}
          companyName={view.companyName}
          onRetry={!fromLocal && view.kind === 'UNKNOWN' ? res.reload : undefined}
        />
      );
    case 'noStaff':
      return (
        <ShareNoticeScreen
          icon={<IconInfoCircle />}
          title={RECEIPT_TEXT.noticeTitle}
          description={RECEIPT_TEXT.noticeStaffMissing}
        />
      );
    case 'notAllowed':
      return (
        <ShareNoticeScreen
          icon={<IconInfoCircle />}
          title={RECEIPT_TEXT.noticeTitle}
          description={RECEIPT_TEXT.noticeNotAllowed}
        />
      );
    case 'received':
      return <ReceivedScreen summary={view.summary} />;
    case 'open':
      break;
  }

  const t = view.target;
  const arrival = arrivalText(t);
  const driver = [t.driverName, t.driverPhone ? (isValidPhone(t.driverPhone) ? formatSharePhone(t.driverPhone) : t.driverPhone) : null].filter(Boolean).join(' ');
  const rows: Array<[string, string]> = [];
  if (t.siteLabel) rows.push([RECEIPT_TEXT.rowSite, t.siteLabel]);
  if (t.workTypeName) rows.push([RECEIPT_TEXT.rowWorkType, t.workTypeName]);
  if (t.vendorName) rows.push([RECEIPT_TEXT.rowVendor, t.vendorName]);
  const items = (t.items ?? []).map(itemText);
  if (arrival) rows.push([RECEIPT_TEXT.rowArrival, arrival]);
  if (driver) rows.push([RECEIPT_TEXT.rowDriver, driver]);

  return (
    <Shell>
      <OpenHeader companyName={t.companyName} foremanName={t.foremanName} />

      <Card>
        <dl className="flex flex-col gap-2">
          {rows.map(([l, v]) => (
            <Row key={l} label={l} value={v} />
          ))}
          {items.length > 0 && (
            <div className="flex gap-3 text-body-sm">
              <dt className="w-16 shrink-0 text-text-muted">{RECEIPT_TEXT.rowItems}</dt>
              <dd className="min-w-0 flex-1 text-text-primary">
                <ul className="flex flex-col gap-1">
                  {items.map((s, i) => (
                    <li key={i} className="break-words">
                      {s}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          )}
        </dl>
      </Card>

      <ReceiptForm
        companyName={t.companyName || undefined}
        onView={showView}
        onDone={(summary) => setOverride({ view: { t: 'received', summary }, source: 'local' })}
      />
    </Shell>
  );
}
